import { convertFiqlUrltoArray } from "common/utils";
import {
  EntityType,
  FilterValueState,
  FilterValueStates,
  SearchFilter,
  useFetchFilters,
} from "core/api";
import { cloneDeep } from "lodash";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import useTableSearchStore from "../Screen/SearchStateStore";
import { useSearchFilters } from "./useSearchFilters";

export interface SearchFilterPanelOptions {
  entityType: EntityType;
  tableOptionId: number;
  historyEntryEntity?: EntityType | undefined;
  disableUrlParameters?: boolean;
  /** Optional: the initial value of the filter */
  initialFilterStates?: FilterValueStates;
  /** Key for searched table state */
  tableSearchStateKey?: string;
}

export interface UseSearchFilterPanelOutputProps {
  /** The current state of the filter search query (to include in the api url) */
  filters: string | string[] | undefined;
  /** TODO [LucaN] understand exactly what this is !!! */
  activeFilterState: FilterValueStates | undefined;
  /** State of the filter fields */
  filterStates: FilterValueStates;
  /** Available filters */
  filterFields: SearchFilter[];
  /** Reset hook: gets called when the user clicks on the RESET button */
  resetFilterPanel: (clearCachedFilters?: boolean) => void;
  /** onChange hook: gets called everytime the user changes the value of a filter field through the UI */
  getOnChange: <TValueState extends FilterValueState>(
    id: number
  ) => (state: TValueState) => void;
  /** Function to set The current state of the filter search string */
  setFilters: Dispatch<SetStateAction<string | string[] | undefined>>;
  /** Function that can be used to generate the filter search query that corresponds to a given filter state */
  formatToFilterQuery: (filterStates: FilterValueStates) => string[];
  /** Function that returns **true** if the given filter states contain
   *  at least one filter that has been set by the user. Returns false if none
   * of the filter fields has been set. */
  hasModifiedFilters: (filterStates: FilterValueStates) => boolean;
}

/** This hook manages the state of the Search Filters of the SearchFilterPanel */
export function useSearchFilterPanel({
  entityType,
  tableOptionId,
  historyEntryEntity,
  disableUrlParameters,
  initialFilterStates = {},
  tableSearchStateKey,
}: SearchFilterPanelOptions): UseSearchFilterPanelOutputProps {
  const { data = [] } = useFetchFilters(entityType, tableOptionId);
  const { searchedFilters } = useTableSearchStore((state) =>
    state.getTableSearchState(tableSearchStateKey ?? "")
  );

  const [manuallyClearedFilters, setManuallyClearedFilters] = useState(false);

  const location = useLocation();
  const showAllSearch = location.state?.showAllSearch ?? false;

  const formattedSearchedFilters = convertFiqlUrltoArray(searchedFilters);

  const appliedFilters =
    Object.keys(initialFilterStates).length === 0
      ? formattedSearchedFilters
      : initialFilterStates;

  const filterFields = useMemo(() => {
    if (historyEntryEntity) {
      return data.filter(
        (filter) =>
          filter.entityType === EntityType.NONE ||
          filter.entityType === historyEntryEntity
      );
    }
    return data;
  }, [data, historyEntryEntity]);

  const { filters, setFilters, activeFilterState, formatToFilterQuery } =
    useSearchFilters(filterFields, disableUrlParameters, initialFilterStates);

  const [filterStates, setFilterStates] = useState<FilterValueStates>({});

  /**
   * Effect that applies filters from `appliedFilters` to `filterStates` if:
   * - `filterStates` is empty,
   * - `appliedFilters` is non-empty,
   * - Filters have not been manually cleared,
   * - At least one filter in `appliedFilters` matches a field in `filterFields` based on `filterId`.
   */
  useEffect(() => {
    if (
      Object.keys(filterStates).length === 0 &&
      Object.keys(appliedFilters).length > 0 &&
      manuallyClearedFilters === false &&
      !showAllSearch &&
      Object.values(appliedFilters).some(
        (filter) =>
          filterFields.some((field) => field.filterId == filter.filterId) // Loose equality for type conversion. One has filterId as number and one as a string.
      )
    ) {
      setFilterStates(appliedFilters);
    }
  }, [filterStates, appliedFilters, manuallyClearedFilters, showAllSearch]);

  useEffect(() => {
    setFilterStates(cloneDeep(activeFilterState ?? {}));
  }, [activeFilterState]);

  useEffect(() => {
    if (manuallyClearedFilters) {
      setManuallyClearedFilters(false);
    }
  }, [searchedFilters]);
  /**
   * Updates the filterState with the value of the filter field identified
   * by **id**. The filter field value is available in **state**.
   *
   * @remarks
   * If the FilterValueState **state** is empty, i.e. the filter field does not have any value,
   * then the corresponding state is removed from the array.
   * Else (if the FilterValueState **state** is NOT empty), the state it is added to the array.
   */
  const getOnChange = useCallback(
    <TValueState extends FilterValueState>(id: number) =>
      (state: TValueState) => {
        if (isFilterFieldEmpty(state)) {
          setManuallyClearedFilters(true);
          delete filterStates[id];
          setFilterStates({ ...filterStates });
        } else {
          setFilterStates((currentState) => ({
            ...currentState,
            [id]: state,
          }));
        }
      },
    [filterStates]
  );

  /**
   * Resets the filter panel.
   *
   * If `clearCachedFilters` is true, sets the flag 'manuallyClearedFilters' as 'true' and resets the filter states.
   * Otherwise, only resets the filter states without clearing the cached filters.
   *
   * @param clearCachedFilters
   */
  const resetFilterPanel = useCallback(
    (clearCachedFilters: boolean = false) => {
      if (clearCachedFilters) {
        setManuallyClearedFilters(true);
        setFilterStates({});
      } else {
        setFilterStates({});
      }
    },
    []
  );

  return {
    filters,
    activeFilterState,
    filterFields,
    filterStates,
    resetFilterPanel,
    getOnChange,
    setFilters,
    formatToFilterQuery,
    hasModifiedFilters,
  };
}

function isFilterFieldEmpty(state: FilterValueState): boolean {
  if (
    !state.value &&
    !state.currencyCode &&
    !state.minValue &&
    !state.maxValue
  ) {
    return true;
  }
  return false;
}

function hasModifiedFilters(filterStates: FilterValueStates) {
  return (
    Object.values(filterStates).filter(
      ({ value, maxValue, minValue, currencyCode }) =>
        (value && (value === true || value?.length > 0)) ||
        minValue ||
        maxValue ||
        currencyCode
    ).length > 0
  );
}
